import type { InputHTMLAttributes } from 'react';
import { forwardRef, useCallback } from 'react';

import type { ColorName } from 'styles/config/colors';

import type { CompoundType } from '@polyai/common/types/helpers';

import * as Styled from './KeyboardSafeInput.styled';

type HTMLPropTypes = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onKeyPress' | 'onKeyPressCapture' | 'onKeyDownCapture' | 'onKeyUpCapture'
>;

type PropTypes = CompoundType<
  HTMLPropTypes & {
    isKeyboardSafe?: boolean;
    textColor?: ColorName;
    placeholderColor?: ColorName;
  }
>;

/**
 * This component stops propagation of keyboard events to the document
 * so that our keyboard shortcut listeners are not triggered.
 *
 * It should be used instead of <input/> tags unless absolutely
 * necessary.
 */
export const KeyboardSafeInput = forwardRef<HTMLInputElement, PropTypes>(
  ({ textColor, placeholderColor, ...props }, ref) => {
    const wrapKeyboardFn = useCallback(
      (fnName: keyof HTMLPropTypes) =>
        (...args: any[]) => {
          props[fnName]?.(...args);
          if (!props.isKeyboardSafe) {
            args[0]?.stopPropagation?.();
          }
        },
      // biome-ignore lint/correctness/useExhaustiveDependencies: existing
      [props],
    );

    const { isKeyboardSafe: _, ...restProps } = props;

    return (
      <Styled.SafeInput
        {...restProps}
        ref={ref}
        $placeholderColor={placeholderColor}
        $textColor={textColor}
        role="input"
        onKeyDown={wrapKeyboardFn('onKeyDown')}
        onKeyUp={wrapKeyboardFn('onKeyUp')}
      />
    );
  },
);
